import React from 'react';
import type {
  FieldValues,
  Path,
  PathValue,
  UseFormSetValue,
} from 'react-hook-form';

/**
 * メールアドレス入力フィールド用のカスタムフック
 * - 全角英数字を半角英数字に変換
 * - メールアドレスに使用できる文字のみ許可（英数字、@、.、-、_、+）
 * - 小文字変換
 * - IME変換中は変換を抑制
 */
export function useEmailInput<T extends FieldValues>({
  fieldName,
  setFormValue,
  onBlurCallback,
}: {
  fieldName: Path<T>;
  setFormValue: UseFormSetValue<T>;
  onBlurCallback?: (event: React.FocusEvent<HTMLInputElement>) => void;
}) {
  const [isComposing, setIsComposing] = React.useState(false);

  const normalizeEmail = (value: string) =>
    value
      // 全角英数字を半角に変換
      .replace(/[Ａ-Ｚａ-ｚ０-９]/g, (match) =>
        String.fromCharCode(match.charCodeAt(0) - 0xfee0)
      )
      // メールアドレスに使用できる文字のみ許可
      .replace(/[^a-zA-Z0-9@.\-_+]/g, '')
      // 小文字変換
      .toLowerCase();

  const handleInput = (event: React.FormEvent<HTMLInputElement>) => {
    const rawValue = event.currentTarget.value;
    if (isComposing) {
      setFormValue(fieldName, rawValue as PathValue<T, Path<T>>);
      return;
    }

    const newValue = normalizeEmail(rawValue);
    setFormValue(fieldName, newValue as PathValue<T, Path<T>>);
  };

  const handleCompositionEnd = (
    event: React.CompositionEvent<HTMLInputElement>
  ) => {
    setIsComposing(false);
    const newValue = normalizeEmail(event.currentTarget.value);
    setFormValue(fieldName, newValue as PathValue<T, Path<T>>);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const trimmedValue = event.currentTarget.value.trim();
    if (trimmedValue !== event.currentTarget.value) {
      setFormValue(fieldName, trimmedValue as PathValue<T, Path<T>>);
    }
    onBlurCallback?.(event);
  };

  const handleFocus = () => {
    setIsComposing(false);
  };

  const handleCompositionStart = () => {
    setIsComposing(true);
  };

  return {
    emailInputProps: {
      name: fieldName,
      onFocus: handleFocus,
      onCompositionStart: handleCompositionStart,
      onCompositionEnd: handleCompositionEnd,
      onInput: handleInput,
      onBlur: handleBlur,
    },
  };
}
